/* Admin · Major Custodian Applied Kit — download, email dispatch (SRN), seal upload, signed-ack status. */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileDown, ShieldCheck, Loader2, Stamp, Send, Upload, FileCheck2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

export function CustodianKitCard({ api, user }) {
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(null);
  const sealRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/admin/users/${user.id}/custodian-kit`).then((r) => setMeta(r.data)).catch(() => setMeta(null));
  }, [api, user.id]);
  useEffect(() => { load(); }, [load]);

  if (!meta) return null;

  const download = async () => {
    setBusy("download");
    try {
      const r = await api.get(`/admin/users/${user.id}/custodian-kit.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${meta.ref}-Major-Custodian-Applied-Kit.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Major Custodian Applied Kit generated"); load();
    } catch (e) { toast.error("Could not generate the kit"); }
    finally { setBusy(null); }
  };

  const dispatch = async () => {
    if (!window.confirm(`Email the Major Custodian Applied Kit to ${meta.email} with an SRN-tracked cover note?`)) return;
    setBusy("dispatch");
    try {
      const { data } = await api.post(`/admin/users/${user.id}/custodian-kit/dispatch`);
      toast.success(`Kit dispatched · ${data.dispatch.srn}`); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Dispatch failed");
    } finally { setBusy(null); }
  };

  const uploadSeal = async (file) => {
    if (!file) return;
    setBusy("seal");
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/admin/treasury-onboarding/seal", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sovereign Seal uploaded — the certificate will carry it from the next download"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Seal upload failed"); }
    finally { setBusy(null); if (sealRef.current) sealRef.current.value = ""; }
  };

  const ack = meta.acknowledgement;
  const disp = meta.last_dispatch;
  return (
    <div data-testid={`custodian-kit-${user.id}`} className="mt-6 rounded-xl border border-[#C9A961]/35 bg-[#C9A961]/[0.05] p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-5">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9A961]"><ShieldCheck className="w-3.5 h-3.5" /> Major Custodian Framework · Active</div>
          <div className="font-serif text-xl text-[#F5F5F0] mt-1">Major Custodian Applied Kit</div>
          <div className="font-body text-xs text-[#8A8A82] mt-1">
            {meta.ref} · Effective {meta.effective_date} · Major Custodian <span className="text-[#C9C9C2]">{meta.custodian}</span> · Signatory <span className="text-[#C9C9C2]">{meta.signatory}</span>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {meta.documents.map((d, i) => (
              <span key={d} className="font-grotesk text-[9px] uppercase tracking-[0.2em] rounded-full border border-white/15 px-2.5 py-1 text-[#C9C9C2]">{i + 1} · {d}</span>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap gap-2 justify-end">
            <button data-testid={`custodian-kit-download-${user.id}`} onClick={download} disabled={!!busy}
                    className="inline-flex items-center gap-2 rounded-full bg-[#F5F5F0] text-black px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.22em] hover:bg-[#C9A961] transition-colors disabled:opacity-50">
              {busy === "download" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Download Kit (PDF)
            </button>
            <button data-testid={`custodian-kit-dispatch-${user.id}`} onClick={dispatch} disabled={!!busy}
                    className="inline-flex items-center gap-2 rounded-full border border-[#C9A961]/60 text-[#C9A961] px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.22em] hover:bg-[#C9A961]/10 transition-colors disabled:opacity-50">
              {busy === "dispatch" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Dispatch by Email
            </button>
          </div>
          <a data-testid={`custodian-kit-open-${user.id}`} href={`${API}${meta.download_url}`} target="_blank" rel="noreferrer"
             className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82] hover:text-[#F5F5F0]">Open in new tab</a>
          {disp && (
            <span data-testid={`custodian-kit-dispatch-status-${user.id}`} className="font-grotesk text-[9px] uppercase tracking-[0.18em] text-[#2FE4C6]">
              Dispatched {new Date(disp.sent_at).toLocaleString("en-IN")} · <span style={{ fontFamily: MONO }}>{disp.srn}</span>
            </span>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3 pt-3 border-t border-white/10">
        <div className="rounded-lg border border-white/10 bg-black/30 p-3 flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 font-grotesk text-[9px] uppercase tracking-[0.2em]" data-testid={`custodian-kit-seal-${user.id}`}
               style={{ color: meta.seal_present ? "#2FE4C6" : "#E0A458" }}>
            <Stamp className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>{meta.seal_present ? `Sovereign Seal on file · ${meta.seal_source}` : "Seal PNG not on file — certificate uses the programmatic Vinci seal"}</span>
          </div>
          <label className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#C9C9C2] hover:border-[#C9A961] cursor-pointer">
            {busy === "seal" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} {meta.seal_present ? "Replace seal" : "Upload seal PNG"}
            <input ref={sealRef} data-testid={`custodian-kit-seal-input-${user.id}`} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadSeal(e.target.files?.[0])} />
          </label>
        </div>
        <div className="rounded-lg border border-white/10 bg-black/30 p-3 flex items-start justify-between gap-3" data-testid={`custodian-kit-ack-${user.id}`}>
          <div className="flex items-start gap-2 font-grotesk text-[9px] uppercase tracking-[0.2em]" style={{ color: ack ? "#2FE4C6" : "#8A8A82" }}>
            <FileCheck2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>{ack ? `Signed Acknowledgement filed ${new Date(ack.uploaded_at).toLocaleString("en-IN")} · ${ack.srn}` : "Signed Acknowledgement · awaiting upload by RealtyF5 via Document Vault"}</span>
          </div>
          {ack && (
            <a data-testid={`custodian-kit-ack-download-${user.id}`} href={`${API}/api/admin/users/${user.id}/custodian-kit/acknowledgement`}
               className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#2FE4C6]/50 text-[#2FE4C6] px-3 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.2em] hover:bg-[#2FE4C6]/10">
              <FileDown className="w-3 h-3" /> Download
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
